import { Tooltip } from 'devextreme-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../../services/api-configuration';

const SevkEmriErrorIndicator = React.memo(() => {
    const [errors, setErrors] = useState([]);
    const [errorType, setErrorType] = useState(null); // null, 'connection', 'api'
    const intervalRef = useRef(null);

    const fetchErrors = useCallback(async () => {
        try {
            setErrorType(null);
            const response = await api.post("/getOCMessageErrors", {});
            const result = response?.data || [];

            if (result && Array.isArray(result) && result.length > 0) {
                setErrors(result);
            } else {
                setErrors([]);
            }
        } catch (error) {
            console.error("Sevk emri error fetch error:", error);
            setErrors([]);
            // Servis bağlantı sorunu kontrolü
            const isConnectionError =
                error.code === 'ERR_NETWORK' ||
                error.message?.includes('Network Error') ||
                error.message?.includes('timeout') ||
                !error.response;

            setErrorType(isConnectionError ? 'connection' : 'api');
        }
    }, []);

    useEffect(() => {
        fetchErrors();

        intervalRef.current = setInterval(() => {
            fetchErrors();
        }, 5000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [fetchErrors]);

    const hasErrors = errors.length > 0;

    // Renk belirleme: Servis bağlantı sorunu -> Kırmızı, Normal hata -> Turuncu, Hata yok -> Yeşil
    let indicatorColor, textColor, shadowColor, statusText;
    if (errorType === 'connection') {
        indicatorColor = '#dc3545';
        textColor = '#dc3545';
        shadowColor = 'rgba(220, 53, 69, 0.7)';
        statusText = 'Sevk Emri Bağlantı Hatası';
    } else if (errorType === 'api') {
        indicatorColor = '#fd7e14';
        textColor = '#fd7e14';
        shadowColor = 'rgba(253, 126, 20, 0.7)';
        statusText = 'Sevk Emri Hata';
    } else if (hasErrors) {
        indicatorColor = '#fd7e14';
        textColor = '#fd7e14';
        shadowColor = 'rgba(253, 126, 20, 0.7)';
        statusText = 'Sevk Emri Error';
    } else {
        indicatorColor = '#28a745';
        textColor = '#28a745';
        shadowColor = 'rgba(40, 167, 69, 0.6)';
        statusText = 'Sevk Emri Error Yok';
    }

    // Tooltip içeriği formatla
    const formatTooltipContent = () => {
        if (errors.length === 0) return 'Hata yok';

        return errors.map((item, index) => {
            const lines = [];
            if (item.sase) lines.push(`Şase: ${item.sase}`);
            if (item.error_detail) lines.push(`Hata: ${item.error_detail}`);
            if (item.warning) lines.push(`Uyarı: ${item.warning}`);

            if (lines.length === 0) return 'Bilinmeyen hata';

            // Her hata için ayrı bir blok
            return lines.join('\n');
        }).join('\n\n---\n\n');
    };

    return (
        <div
            id="sevk-emri-error-indicator"
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                position: 'relative'
            }}
        >
            <div
                style={{
                    width: (errorType || hasErrors) ? '10px' : '8px',
                    height: (errorType || hasErrors) ? '10px' : '8px',
                    borderRadius: '50%',
                    backgroundColor: indicatorColor,
                    animation: (errorType || hasErrors) ? 'blink 1s infinite' : 'blink 1.5s infinite',
                    boxShadow: `0 0 ${(errorType || hasErrors) ? '6px' : '4px'} ${shadowColor}`,
                    flexShrink: 0
                }}
            />
            <span
                style={{
                    fontSize: '12px',
                    fontWeight: (errorType || hasErrors) ? '600' : '500',
                    color: textColor,
                    whiteSpace: 'nowrap',
                    letterSpacing: '0.3px',
                    cursor: (hasErrors || errorType) ? 'pointer' : 'default',
                    userSelect: 'none'
                }}
                onClick={(hasErrors || errorType) ? () => {
                    const textToCopy = errorType ? 'Hata oluştu' : formatTooltipContent();
                    navigator.clipboard.writeText(textToCopy).then(() => {
                        toast.success('Kopyalandı');
                    }).catch(() => {
                        toast.error('Kopyalama başarısız');
                    });
                } : undefined}
            >
                {statusText}
            </span>
            {(hasErrors || errorType) && (
                <Tooltip
                    target="#sevk-emri-error-indicator"
                    showEvent="mouseenter"
                    hideEvent="mouseleave"
                >
                    <div style={{
                        padding: '8px',
                        maxWidth: '500px',
                        whiteSpace: 'pre-line',
                        fontSize: '12px',
                        lineHeight: '1.6',
                        fontFamily: 'monospace'
                    }}>
                        <div style={{
                            fontSize: '12px',
                            color: '#6c757d',
                            fontStyle: 'italic',
                            borderBottom: '1px solid #e9ecef',
                            paddingBottom: '6px',
                            marginBottom: '6px',
                            whiteSpace: 'normal'
                        }}>
                            Tıklayarak kopyala
                        </div>
                        <div style={{ marginBottom: '6px' }}>
                            {errorType ? 'Hata oluştu' : formatTooltipContent()}
                        </div>

                    </div>
                </Tooltip>
            )}
            <style>{`
                @keyframes blink {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.4; }
                }
            `}</style>
        </div>
    );
});

SevkEmriErrorIndicator.displayName = 'SevkEmriErrorIndicator';

export default SevkEmriErrorIndicator;

